import React, { useState, useEffect } from 'react';
import { Col, Descriptions, Tabs, Tag } from 'antd';
import useGlobal from '../../../store/Store';
import { Results } from '../../../common/results/Results';
import { ClassificationResult } from '../../../common/results/ClassificationResult';
import { ClassificationResult as ClassificationResultType, InferenceFile } from '../../../types';
import { MultiflowIteratorResult, MultiflowStatus, MultiflowValidationResult } from '../../../types/flow';
import axios from 'axios';
import { FlowPath } from '../../../components/FlowPath';
import { ResultTables } from '../../../common/results/ResultTables';
import { CustomSider } from '../../../components/layout/CustomSider';
import { Section } from '../../../components/Section';
import { ResultProperties } from '../../../common/results/ResultProperties';

const { TabPane } = Tabs;

type Props = {
  inferenceFile: null | InferenceFile;
  multiflowStatus: null | MultiflowStatus;
};

const ResultSideBar = (props: Props) => {
  const { inferenceFile, multiflowStatus } = props;

  const [{ readOnly }] = useGlobal();

  const fileName = inferenceFile && inferenceFile.fileName;
  const modelName = inferenceFile && inferenceFile.model && inferenceFile.model.name;
  const modelVersion = inferenceFile && inferenceFile.modelVersion && inferenceFile.modelVersion.name;

  const [, setMultiValidationResult] = useState<null | MultiflowValidationResult>(null);
  const [multiIteratorResult, setMultiIteratorResult] = useState<null | MultiflowIteratorResult>(null);

  useEffect(() => {
    const fetchMultiValidation = async () => {
      if (multiflowStatus) {
        const { data } = await axios(multiflowStatus.validationResultUrl);
        const validationResult: MultiflowValidationResult = data;
        setMultiValidationResult(validationResult);
      }
    };
    const fetchMultiIterator = async () => {
      if (multiflowStatus) {
        const { data } = await axios(multiflowStatus.iteratorResultUrl);
        const iteratorResult: MultiflowIteratorResult = data;
        setMultiIteratorResult(iteratorResult);
      }
    };
    fetchMultiValidation();
    fetchMultiIterator();
  }, [inferenceFile, multiflowStatus]);

  const isMultiflow = () => {
    return (
      inferenceFile && inferenceFile.model && inferenceFile.model.algorithm && inferenceFile.model.algorithm.multiflow
    );
  };

  const getClassification = () => {
    return inferenceFile && inferenceFile.annotations && inferenceFile.annotations.type;
  };

  return (
    <CustomSider width={400} border="left" style={{ paddingTop: '1.25rem' }}>
      <Section>
        <Col>
          <Descriptions layout="horizontal" column={1}>
            <Descriptions.Item label="File name">{fileName}</Descriptions.Item>
            <Descriptions.Item label="Model name">{modelName}</Descriptions.Item>
            <Descriptions.Item label="Version">{modelVersion}</Descriptions.Item>
            {isMultiflow() && inferenceFile?.inferenceResult?.result.type && (
              <Descriptions.Item label="Classes">
                {(inferenceFile?.inferenceResult?.result.type as ClassificationResultType[]).map(type => (
                  <Tag key={type.name}>{type.name}</Tag>
                ))}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Col>
      </Section>
      {!isMultiflow() && getClassification() ? (
        <ClassificationResult
          readOnly={readOnly}
          result={getClassification()}
          onClassChange={() => {}}
          classes={
            (inferenceFile &&
              inferenceFile.model &&
              inferenceFile.model.info &&
              inferenceFile.model.info.annotationTypes) ||
            []
          }
        />
      ) : (
        <Col>
          {isMultiflow() && <FlowPath inferenceFile={inferenceFile} multiIteratorResult={multiIteratorResult} />}
          {isMultiflow() || inferenceFile?.model.algorithm.id === 1 ? (
            <>
              {!inferenceFile?.inferenceResult?.result?.tables &&
              !inferenceFile?.inferenceResult?.result?.properties ? (
                <Results
                  onAccept={() => {}}
                  onDelete={() => {}}
                  onEdit={() => {}}
                  datasetMode={false}
                  beautifyLabels={true}
                  inTable={false}
                />
              ) : (
                <Tabs
                  size="large"
                  defaultActiveKey="1"
                  tabBarStyle={{ padding: '0 20px', color: 'rgba(0, 94, 160, 1)', fontWeight: 'bold' }}>
                  <TabPane tab="Fields" key="1">
                    <Results
                      onAccept={() => {}}
                      onDelete={() => {}}
                      onEdit={() => {}}
                      datasetMode={false}
                      beautifyLabels={true}
                      inTable={true}
                    />
                  </TabPane>
                  {inferenceFile?.inferenceResult?.result?.tables && (
                    <TabPane tab="Tables" key="2">
                      <ResultTables inferenceFile={inferenceFile} />
                    </TabPane>
                  )}
                  {inferenceFile?.inferenceResult?.result?.properties && (
                    <TabPane tab="Properties" key="3">
                      <ResultProperties inferenceFile={inferenceFile} />
                    </TabPane>
                  )}
                </Tabs>
              )}
            </>
          ) : (
            <>
              <Results
                onAccept={() => {}}
                onDelete={() => {}}
                onEdit={() => {}}
                datasetMode={false}
                beautifyLabels={true}
                inTable={false}
              />
            </>
          )}
        </Col>
      )}
    </CustomSider>
  );
};

export default ResultSideBar;
